/* eslint-disable import/no-cycle */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { notification } from 'antd';
import API from '../services';

const initialState = {
  invites: [],
  discordUser: null,
  verificationInfo: null,
  isInviteLoading: false,
  isDiscordUserLoading: false,
  isVerificationInfoLoading: false,
  isQualifiedForCommunity: false,
  isQualifiedForCommunityLoading: true,
  allInvites: [],
  isAllInvitesLoading: false,
  allDiscordUsers: [],
  isAllDiscordUsersLoading: false,
  userInvites: [],
  isUserInvitesLoading: false,
  userDiscordUser: null,
  isUserDiscordUserLoading: false,
};

export const getUserInvites = createAsyncThunk('community/getUserInvites', async () => {
  return API.CommunityService.getInvites();
});

export const getDiscordUser = createAsyncThunk('community/getDiscordUser', async () => {
  return API.CommunityService.getDiscordUser();
});

export const getVerificationInfo = createAsyncThunk('community/getVerificationInfo', async () => {
  return API.CommunityService.getVerificationInfo();
});

export const verifyDiscordUser = createAsyncThunk('community/verifyDiscordUser', async () => {
  return API.CommunityService.verifyDiscordUser();
});

export const createInvite = createAsyncThunk('community/createInvite', async () => {
  return API.CommunityService.createInvite();
});

export const getIsUserQualifiedForCommunity = createAsyncThunk('community/getIsUserQualifiedForCommunity', async () => {
  return API.CommunityService.getIsUserQualifiedForCommunity();
});

export const getAllInvites = createAsyncThunk(
  'community/getAllInvites',
  async ({ page, limit, search, inviteCode }) => {
    return API.CommunityService.getAllInvites({
      page,
      limit,
      search,
      inviteCode,
    });
  },
);

export const getAllDiscordUsers = createAsyncThunk(
  'community/getAllDiscordUsers',
  async ({ page, limit, search, status }) => {
    return API.CommunityService.getAllDiscordUsers({
      page,
      limit,
      search,
      status,
    });
  },
);

export const refreshDiscordUserData = createAsyncThunk('community/refreshDiscordUserData', async userId => {
  return API.CommunityService.refreshDiscordUserData(userId);
});

export const overrideDiscordUserStatus = createAsyncThunk(
  'community/overrideDiscordUserStatus',
  async ({ userId, status }) => {
    return API.CommunityService.overrideDiscordUserStatus(userId, status);
  },
);

export const declineInvite = createAsyncThunk('community/declineInvite', async inviteId => {
  return API.CommunityService.declineInvite(inviteId);
});

export const unbanDiscordUser = createAsyncThunk('community/unbanDiscordUser', async userId => {
  return API.CommunityService.unbanDiscordUser(userId);
});

export const getUserDiscordUserById = createAsyncThunk('community/getUserDiscordUserById', async userId => {
  return API.CommunityService.getUserDiscordUserById(userId);
});

export const getUserDiscordInvitesById = createAsyncThunk('community/getUserDiscordInvitesById', async userId => {
  return API.CommunityService.getUserDiscordInvitesById(userId);
});

export const updateUserExamYearOnDiscord = createAsyncThunk('community/updateUserExamYearOnDiscord', async userId => {
  return API.CommunityService.updateUserExamYearOnDiscord(userId);
});

const communitySlice = createSlice({
  name: 'community',
  initialState,
  extraReducers: {
    [getUserInvites.pending.toString()]: state => {
      state.isInviteLoading = true;
    },
    [getUserInvites.fulfilled.toString()]: (state, action) => {
      state.isInviteLoading = false;
      state.invites = action.payload.data || [];
    },
    [getUserInvites.rejected.toString()]: state => {
      state.isInviteLoading = false;
      notification.error({
        message: 'Nie udało się wczytać zaproszeń!',
        placement: 'bottomRight',
      });
    },
    [getDiscordUser.pending.toString()]: state => {
      state.isDiscordUserLoading = true;
    },
    [getDiscordUser.fulfilled.toString()]: (state, action) => {
      state.isDiscordUserLoading = false;
      state.discordUser = action.payload.data || null;
    },
    [getDiscordUser.rejected.toString()]: (state, action) => {
      state.isDiscordUserLoading = false;
      notification.error({
        message: 'Nie udało się wczytać użytkownika Discord!',
        description: action.error.message,
        placement: 'bottomRight',
      });
    },
    [getVerificationInfo.pending.toString()]: state => {
      state.isVerificationInfoLoading = true;
    },
    [getVerificationInfo.fulfilled.toString()]: (state, action) => {
      state.isVerificationInfoLoading = false;
      state.verificationInfo = action.payload.data || null;
    },
    [getVerificationInfo.rejected.toString()]: (state, action) => {
      state.isVerificationInfoLoading = false;
      notification.error({
        message: 'Nie udało się wczytać informacji o weryfikacji!',
        description: action.error.message,
        placement: 'bottomRight',
      });
    },
    [verifyDiscordUser.pending.toString()]: state => {
      state.isVerificationInfoLoading = true;
    },
    [verifyDiscordUser.fulfilled.toString()]: state => {
      state.isVerificationInfoLoading = false;
      notification.success({
        message: 'Użytkownik zweryfikowany!',
        description: 'Twoje konto Discord zostało pomyślnie zweryfikowane.',
        placement: 'bottomRight',
      });
    },
    [verifyDiscordUser.rejected.toString()]: (state, action) => {
      state.isVerificationInfoLoading = false;
      notification.error({
        message: 'Nie udało się zweryfikować użytkownika!',
        description: action.error.message,
        placement: 'bottomRight',
      });
    },
    [createInvite.pending.toString()]: state => {
      state.isInviteLoading = true;
    },
    [createInvite.fulfilled.toString()]: (state, action) => {
      state.isInviteLoading = false;
      notification.success({
        message: 'Zaproszenie utworzone!',
        description: 'Twoje zaproszenie zostało pomyślnie utworzone.',
        placement: 'bottomRight',
      });
      state.invites.push(action.payload.data?.invite);
    },
    [createInvite.rejected.toString()]: (state, action) => {
      state.isInviteLoading = false;
      notification.error({
        message: 'Nie udało się utworzyć zaproszenia!',
        description: action.error.message,
        placement: 'bottomRight',
      });
    },
    [getIsUserQualifiedForCommunity.pending.toString()]: state => {
      state.isQualifiedForCommunityLoading = true;
    },
    [getIsUserQualifiedForCommunity.fulfilled.toString()]: (state, action) => {
      state.isQualifiedForCommunityLoading = false;
      state.isQualifiedForCommunity = action.payload.data.isQualified || false;
    },
    [getIsUserQualifiedForCommunity.rejected.toString()]: (state, action) => {
      state.isQualifiedForCommunityLoading = false;
    },
    [getAllInvites.pending.toString()]: state => {
      state.isAllInvitesLoading = true;
    },
    [getAllInvites.fulfilled.toString()]: (state, action) => {
      state.isAllInvitesLoading = false;
      state.allInvites = action.payload.data;
    },
    [getAllInvites.rejected.toString()]: (state, action) => {
      state.isAllInvitesLoading = false;
      notification.error({
        message: 'Nie udało się wczytać zaproszeń!',
        description: action.error.message,
        placement: 'bottomRight',
      });
    },
    [getAllDiscordUsers.pending.toString()]: state => {
      state.isAllDiscordUsersLoading = true;
    },
    [getAllDiscordUsers.fulfilled.toString()]: (state, action) => {
      state.isAllDiscordUsersLoading = false;
      state.allDiscordUsers = action.payload.data;
    },
    [getAllDiscordUsers.rejected.toString()]: (state, action) => {
      state.isAllDiscordUsersLoading = false;
      notification.error({
        message: 'Nie udało się wczytać użytkowników Discord!',
        description: action.error.message,
        placement: 'bottomRight',
      });
    },
    [refreshDiscordUserData.pending.toString()]: state => {
      state.isDiscordUserLoading = true;
    },
    [refreshDiscordUserData.fulfilled.toString()]: state => {
      state.isDiscordUserLoading = false;
      notification.success({
        message: 'Dane użytkownika odświeżone!',
        description: 'Dane użytkownika Discord zostały pomyślnie odświeżone.',
        placement: 'bottomRight',
      });
    },
    [refreshDiscordUserData.rejected.toString()]: (state, action) => {
      state.isDiscordUserLoading = false;
      notification.error({
        message: 'Nie udało się odświeżyć danych użytkownika!',
        description: action.error.message,
        placement: 'bottomRight',
      });
    },
    [overrideDiscordUserStatus.pending.toString()]: state => {
      state.isUserDiscordUserLoading = true;
    },
    [overrideDiscordUserStatus.fulfilled.toString()]: state => {
      state.isUserDiscordUserLoading = false;
      notification.success({
        message: 'Status użytkownika zaktualizowany!',
        description: 'Status użytkownika Discord został pomyślnie zaktualizowany.',
        placement: 'bottomRight',
      });
    },
    [overrideDiscordUserStatus.rejected.toString()]: (state, action) => {
      state.isUserDiscordUserLoading = false;
      notification.error({
        message: 'Nie udało się zaktualizować statusu użytkownika!',
        description: action.error.message,
        placement: 'bottomRight',
      });
    },
    [declineInvite.pending.toString()]: state => {
      state.isInviteLoading = true;
    },
    [declineInvite.fulfilled.toString()]: (state, action) => {
      state.isInviteLoading = false;
      notification.success({
        message: 'Zaproszenie odrzucone!',
        description: 'Zaproszenie zostało pomyślnie odrzucone.',
        placement: 'bottomRight',
      });
      state.invites = state.invites.filter(invite => invite.id !== action.payload.data.inviteId);
    },
    [declineInvite.rejected.toString()]: (state, action) => {
      state.isInviteLoading = false;
      notification.error({
        message: 'Nie udało się odrzucić zaproszenia!',
        description: action.error.message,
        placement: 'bottomRight',
      });
    },
    [unbanDiscordUser.pending.toString()]: state => {
      state.isUserDiscordUserLoading = true;
    },
    [unbanDiscordUser.fulfilled.toString()]: state => {
      state.isUserDiscordUserLoading = false;
      notification.success({
        message: 'Użytkownik odblokowany!',
        description: 'Użytkownik Discord został pomyślnie odblokowany.',
        placement: 'bottomRight',
      });
    },
    [unbanDiscordUser.rejected.toString()]: (state, action) => {
      state.isUserDiscordUserLoading = false;
      notification.error({
        message: 'Nie udało się odblokować użytkownika!',
        description: action.error.message,
        placement: 'bottomRight',
      });
    },
    [getUserDiscordUserById.pending.toString()]: state => {
      state.userDiscordUser = null;
      state.isUserDiscordUserLoading = true;
    },
    [getUserDiscordUserById.fulfilled.toString()]: (state, action) => {
      state.isUserDiscordUserLoading = false;
      state.userDiscordUser = action.payload.data || null;
    },
    [getUserDiscordUserById.rejected.toString()]: (state, action) => {
      state.isUserDiscordUserLoading = false;
      notification.error({
        message: 'Nie udało się wczytać użytkownika Discord!',
        description: action.error.message,
        placement: 'bottomRight',
      });
    },
    [getUserDiscordInvitesById.pending.toString()]: state => {
      state.userInvites = [];
      state.isUserInvitesLoading = true;
    },
    [getUserDiscordInvitesById.fulfilled.toString()]: (state, action) => {
      state.isUserInvitesLoading = false;
      state.userInvites = action.payload.data || [];
    },
    [getUserDiscordInvitesById.rejected.toString()]: (state, action) => {
      state.isUserInvitesLoading = false;
      notification.error({
        message: 'Nie udało się wczytać zaproszeń użytkownika!',
        description: action.error.message,
        placement: 'bottomRight',
      });
    },
    [updateUserExamYearOnDiscord.pending.toString()]: state => {
      state.isUserDiscordUserLoading = true;
    },
    [updateUserExamYearOnDiscord.fulfilled.toString()]: state => {
      state.isUserDiscordUserLoading = false;
      notification.success({
        message: 'Rok egzaminu zaktualizowany!',
        description: 'Rok egzaminu użytkownika Discord został pomyślnie zaktualizowany.',
        placement: 'bottomRight',
      });
    },
    [updateUserExamYearOnDiscord.rejected.toString()]: (state, action) => {
      state.isUserDiscordUserLoading = false;
      notification.error({
        message: 'Nie udało się zaktualizować roku egzaminu!',
        description: action.error.message,
        placement: 'bottomRight',
      });
    },
  },
});

export default communitySlice.reducer;
